import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Users, RefreshCw, AlertCircle, UserX, LogOut, Clock, 
  Monitor, Globe, Radio, ChevronDown, ChevronRight, Filter,
  User, Eye, EyeOff, Camera, History, Activity, Copy, 
  Smartphone, Tablet, Laptop, Cpu
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogFooter, DialogDescription 
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (startedAt, endedAt = null) => {
  if (!startedAt) return 'N/A';
  const start = new Date(startedAt);
  const end = endedAt ? new Date(endedAt) : new Date();
  const diff = Math.floor((end - start) / 1000);
  
  if (diff < 0) return 'N/A';
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
};

// Format device + OS: "iPhone15,3 (iOS 26.2.1)" or "Pixel 8 (Android 14)"
const formatDeviceOS = (session) => {
  const model = session.device_model || 'Unknown';
  
  if (!session.os_version) return model;
  
  // Normalize platform name: capitalize first letter, avoid duplication
  let platformName = '';
  if (session.platform) {
    const p = session.platform.toLowerCase();
    if (p === 'ios') platformName = 'iOS';
    else if (p === 'android') platformName = 'Android';
    else platformName = session.platform.charAt(0).toUpperCase() + session.platform.slice(1);
  }
  
  return `${model} (${platformName} ${session.os_version})`.replace('( ', '(').trim();
};

// Format app metadata line: "App 1.0.0 (123) • v3 • ios"
const formatAppMetadata = (session) => {
  const parts = [];
  
  if (session.app_version) {
    const build = session.build_number ? ` (${session.build_number})` : '';
    parts.push(`App ${session.app_version}${build}`);
  }
  
  if (session.player_version) {
    parts.push(`Player v${session.player_version}`);
  }
  
  if (session.client_type) {
    parts.push(session.client_type);
  }
  
  return parts.length > 0 ? parts.join(' • ') : null;
};

// Format "last seen X ago" from last_seen_at
const formatLastSeen = (lastSeenAt) => {
  if (!lastSeenAt) return null;
  
  const now = new Date();
  const lastSeen = new Date(lastSeenAt);
  const diffSeconds = Math.floor((now - lastSeen) / 1000);
  
  if (diffSeconds < 0) return 'just now';
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  return `${Math.floor(diffSeconds / 86400)}d ago`;
};

// Copy device ID to clipboard
const copyToClipboard = async (text, e) => {
  e?.stopPropagation();
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

// Group sessions by username or device
const groupSessions = (sessions) => {
  const groups = {};
  sessions.forEach(session => {
    // Group key: username if present, otherwise device_id
    const deviceId = session.device_id || session.device || 'unknown';
    const key = session.username || deviceId;
    
    if (!groups[key]) {
      groups[key] = {
        key,
        username: session.username,
        displayName: session.user_display_name || null, // Use display name from API if available
        deviceId: deviceId,
        isAnonymous: !session.username,
        sessions: []
      };
    }
    // Update display name if available (might come from later sessions)
    if (session.user_display_name && !groups[key].displayName) {
      groups[key].displayName = session.user_display_name;
    }
    groups[key].sessions.push(session);
  });
  return Object.values(groups);
};

// Detailed session row with device metadata
// Get session health based on QoE metrics
const getSessionHealth = (session) => {
  const bufferCount = session.qoe_buffer_count || 0;
  const errorCount = session.qoe_error_count || 0;
  
  if (errorCount > 3 || bufferCount > 20) {
    return { status: 'poor', color: 'bg-red-500/20 text-red-400', label: 'Poor' };
  }
  if (errorCount > 0 || bufferCount > 5) {
    return { status: 'fair', color: 'bg-yellow-500/20 text-yellow-400', label: 'Fair' };
  }
  return { status: 'good', color: 'bg-green-500/20 text-green-400', label: 'Good' };
};

// Format connection type for display
const formatConnectionType = (type) => {
  if (!type) return null;
  const types = {
    wifi: '📶 WiFi',
    cellular: '📱 Cellular',
    ethernet: '🔌 Ethernet',
    unknown: '❓ Unknown'
  };
  return types[type.toLowerCase()] || type;
};

const SessionDetailRow = ({ session, onKick, onCopyDeviceId, showKick = true }) => {
  const deviceId = session.device_id || session.device || 'N/A';
  const deviceOS = formatDeviceOS(session);
  const appMetadata = formatAppMetadata(session);
  const lastSeenAge = formatLastSeen(session.last_seen_at);
  
  // QoE metrics
  const hasQoE = session.qoe_buffer_count != null || session.qoe_error_count != null;
  const health = getSessionHealth(session);
  
  return (
    <div className="rounded-lg bg-secondary/30 p-3 text-sm space-y-3">
      {/* Top row: Camera/Station + Duration + Health + Kick */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {session.session_type === 'radio' ? (
            <Radio className="h-4 w-4 text-amber-500" />
          ) : (
            <Camera className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="font-medium">{session.camera_name || session.station_name || session.camera_id || 'Unknown'}</span>
          {session.camera_short_code && (
            <Badge variant="outline" className="text-xs font-mono">
              {session.camera_short_code}
            </Badge>
          )}
          {session.session_type && (
            <Badge variant="outline" className="text-xs">
              {session.session_type}
            </Badge>
          )}
          {session.state && (
            <Badge 
              variant={session.state === 'playing' ? 'default' : 'secondary'} 
              className={session.state === 'playing' ? 'bg-green-500/20 text-green-400 text-xs' : 'text-xs'}
            >
              {session.state}
            </Badge>
          )}
          {hasQoE && (
            <Badge className={`text-xs ${health.color}`}>
              {health.label}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{formatDuration(session.started_at, session.ended_at)}</span>
          </div>
          {showKick && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onKick(session)}
            >
              <LogOut className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
      
      {/* QoE Metrics Row - only show if we have data */}
      {hasQoE && (
        <div className="space-y-3 border-t border-border/50 pt-3">
          {/* Buffer Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
            <div className="rounded bg-secondary/50 p-2">
              <p className="text-muted-foreground">Buffers</p>
              <p className={`font-mono font-medium ${(session.qoe_buffer_count || 0) > 5 ? 'text-yellow-400' : 'text-foreground'}`}>
                {session.qoe_buffer_count || 0}
              </p>
            </div>
            <div className="rounded bg-secondary/50 p-2">
              <p className="text-muted-foreground">Total Buffer</p>
              <p className={`font-mono font-medium ${(session.qoe_buffer_time_seconds || 0) > 30 ? 'text-yellow-400' : 'text-foreground'}`}>
                {(session.qoe_buffer_time_seconds || 0).toFixed(1)}s
              </p>
            </div>
            <div className="rounded bg-secondary/50 p-2">
              <p className="text-muted-foreground">Worst Buffer</p>
              <p className={`font-mono font-medium ${(session.qoe_worst_buffer_seconds || 0) > 5 ? 'text-red-400' : 'text-foreground'}`}>
                {(session.qoe_worst_buffer_seconds || 0).toFixed(1)}s
              </p>
            </div>
            <div className="rounded bg-secondary/50 p-2">
              <p className="text-muted-foreground">Startup</p>
              <p className={`font-mono font-medium ${(session.qoe_startup_time_seconds || 0) > 3 ? 'text-yellow-400' : 'text-green-400'}`}>
                {(session.qoe_startup_time_seconds || 0).toFixed(2)}s
              </p>
            </div>
            <div className="rounded bg-secondary/50 p-2">
              <p className="text-muted-foreground">Errors</p>
              <p className={`font-mono font-medium ${(session.qoe_error_count || 0) > 0 ? 'text-red-400' : 'text-foreground'}`}>
                {session.qoe_error_count || 0}
              </p>
            </div>
          </div>
          
          {/* Connection & Status Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="rounded bg-secondary/50 p-2">
              <p className="text-muted-foreground">Connection</p>
              <p className="font-medium">
                {formatConnectionType(session.qoe_connection_type) || '—'}
              </p>
            </div>
            {session.qoe_buffer_count > 0 && (
              <div className="rounded bg-secondary/50 p-2">
                <p className="text-muted-foreground">Avg Buffer</p>
                <p className="font-mono font-medium">
                  {((session.qoe_buffer_time_seconds || 0) / (session.qoe_buffer_count || 1)).toFixed(2)}s
                </p>
              </div>
            )}
            {session.qoe_exited_during_buffer && (
              <div className="rounded bg-red-500/20 p-2">
                <p className="text-muted-foreground">Rage Quit</p>
                <p className="font-medium text-red-400">Yes</p>
              </div>
            )}
            {session.qoe_last_error && (
              <div className="rounded bg-red-500/10 p-2 col-span-2">
                <p className="text-muted-foreground">Last Error</p>
                <p className="font-mono text-red-400 truncate" title={session.qoe_last_error}>
                  {session.qoe_last_error}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Device info row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground border-t border-border/50 pt-3">
        {/* Device + OS */}
        <div className="flex items-start gap-2 col-span-2 md:col-span-1">
          <Smartphone className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-foreground/80 font-medium">Device</p>
            <p className="break-words">{deviceOS}</p>
            {appMetadata && (
              <p className="text-muted-foreground/70 break-words mt-0.5">{appMetadata}</p>
            )}
          </div>
        </div>
        
        {/* Platform */}
        <div className="flex items-start gap-2">
          <Cpu className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>
            <p className="text-foreground/80 font-medium">Platform</p>
            <p>{session.platform || 'N/A'}</p>
          </div>
        </div>
        
        {/* IP */}
        <div className="flex items-start gap-2">
          <Globe className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>
            <p className="text-foreground/80 font-medium">IP Address</p>
            <p className="font-mono">{session.ip || session.ip_address || 'N/A'}</p>
          </div>
        </div>
        
        {/* Device ID + Copy with tooltip */}
        <div className="flex items-start gap-2">
          <Monitor className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-foreground/80 font-medium">Device ID</p>
            <div className="flex items-center gap-1 group/deviceid">
              <p 
                className="truncate font-mono text-[11px] max-w-[120px] cursor-help" 
                title={deviceId}
              >
                {deviceId}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 hover:bg-secondary"
                onClick={(e) => onCopyDeviceId(deviceId, e)}
                title="Copy device_id"
                data-testid={`copy-device-id-${deviceId}`}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Timestamps row */}
      <div className="flex items-center gap-6 text-xs text-muted-foreground border-t border-border/50 pt-2">
        <div>
          <span className="text-foreground/60">Started:</span>{' '}
          <span>{formatDate(session.started_at)}</span>
        </div>
        <div>
          <span className="text-foreground/60">Last seen:</span>{' '}
          <span>
            {lastSeenAge || formatDate(session.last_seen_at || session.ended_at)}
          </span>
        </div>
        {session.duration_seconds != null && (
          <div>
            <span className="text-foreground/60">Duration:</span>{' '}
            <span>{Math.round(session.duration_seconds / 60)}m</span>
          </div>
        )}
      </div>
    </div>
  );
};

const UserSessionGroup = ({ group, onKickSession, onKickUser, onCopyDeviceId, showKick = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Format group label: display name or username or "Anonymous • <device_id>"
  const groupLabel = group.isAnonymous 
    ? `Anonymous • ${group.deviceId}`
    : group.displayName || group.username;
  
  // Show username underneath display name if different
  const showUsername = group.displayName && group.displayName !== group.username;
  
  // Handle row click to toggle expansion
  const handleRowClick = (e) => {
    // Don't toggle if clicking the kick button
    if (e.target.closest('button')) return;
    setIsOpen(!isOpen);
  };
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <CollapsibleTrigger asChild>
          <div 
            className="flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors cursor-pointer"
            onClick={handleRowClick}
            data-testid={`session-group-${group.key}`}
          >
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
                {group.isAnonymous ? (
                  <EyeOff className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <User className="h-5 w-5 text-primary" />
                )}
              </div>
              <div className="text-left">
                <p className="font-medium text-foreground">
                  {groupLabel}
                </p>
                {showUsername && (
                  <p className="text-xs text-muted-foreground">@{group.username}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  {group.sessions.length} session{group.sessions.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {showKick && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onKickUser(group.username || group.key);
                  }}
                  data-testid={`kick-user-${group.key}`}
                >
                  <UserX className="mr-1 h-4 w-4" />
                  Kick All
                </Button>
              )}
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border bg-background/50 p-4 space-y-3">
            {group.sessions.map((session, idx) => (
              <SessionDetailRow 
                key={session._id || session.id || session.session_id || idx} 
                session={session}
                onKick={onKickSession}
                onCopyDeviceId={onCopyDeviceId}
                showKick={showKick}
              />
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export function Sessions() {
  const [activeTab, setActiveTab] = useState('active');
  const [activeSessions, setActiveSessions] = useState([]);
  const [historySessions, setHistorySessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kickSession, setKickSession] = useState(null);
  const [kickUser, setKickUser] = useState(null);
  const [isKicking, setIsKicking] = useState(false);
  const [isEndingStale, setIsEndingStale] = useState(false);
  
  // Filters
  const [filterCamera, setFilterCamera] = useState('all');
  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAnonymousOnly, setShowAnonymousOnly] = useState(false);
  const [showProblemsOnly, setShowProblemsOnly] = useState(false);

  // Pagination for history
  const [historyPage, setHistoryPage] = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const HISTORY_LIMIT = 100;

  // Copy device ID handler
  const handleCopyDeviceId = async (deviceId, e) => {
    const success = await copyToClipboard(deviceId, e);
    if (success) {
      toast.success('Device ID copied to clipboard');
    } else {
      toast.error('Failed to copy device ID');
    }
  };

  const fetchActiveSessions = useCallback(async () => {
    try {
      const data = await api.getActiveSessions();
      const sessions = Array.isArray(data) ? data : data.sessions || [];
      setActiveSessions(sessions);
    } catch (err) {
      console.error('Active sessions error:', err);
      throw err;
    }
  }, []);

  const fetchHistorySessions = useCallback(async (page = 0, append = false) => {
    try {
      const offset = page * HISTORY_LIMIT;
      const data = await api.getSessionHistory(HISTORY_LIMIT, offset);
      const sessions = Array.isArray(data) ? data : data.sessions || [];
      
      if (append) {
        setHistorySessions(prev => [...prev, ...sessions]);
      } else {
        setHistorySessions(sessions);
      }
      
      setHasMoreHistory(sessions.length === HISTORY_LIMIT);
    } catch (err) {
      console.error('History sessions error:', err);
      throw err;
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (activeTab === 'active') {
        await fetchActiveSessions();
      } else {
        await fetchHistorySessions(0, false);
        setHistoryPage(0);
      }
    } catch (err) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, fetchActiveSessions, fetchHistorySessions]);

  useEffect(() => {
    fetchSessions();
    // Auto-refresh only for active sessions
    if (activeTab === 'active') {
      const interval = setInterval(fetchActiveSessions, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab, fetchSessions, fetchActiveSessions]);

  const loadMoreHistory = async () => {
    const nextPage = historyPage + 1;
    setHistoryPage(nextPage);
    await fetchHistorySessions(nextPage, true);
  };

  // Get current sessions based on active tab
  const currentSessions = activeTab === 'active' ? activeSessions : historySessions;

  // Get unique cameras for filter
  const cameras = useMemo(() => {
    const cameraSet = new Set();
    currentSessions.forEach(s => {
      const name = s.camera_name || s.station_name || s.camera_id;
      if (name) cameraSet.add(name);
    });
    return Array.from(cameraSet);
  }, [currentSessions]);

  // Apply filters
  const filteredSessions = useMemo(() => {
    return currentSessions.filter(s => {
      // Camera filter
      if (filterCamera !== 'all') {
        const sessionCamera = s.camera_name || s.station_name || s.camera_id;
        if (sessionCamera !== filterCamera) return false;
      }
      // User filter
      if (filterUser) {
        const username = s.username || s.device_id || s.device || '';
        if (!username.toLowerCase().includes(filterUser.toLowerCase())) return false;
      }
      // Type filter
      if (filterType !== 'all') {
        if (s.session_type !== filterType) return false;
      }
      // Anonymous filter
      if (showAnonymousOnly && s.username) return false;
      
      // Problem sessions filter - show only sessions with QoE issues
      if (showProblemsOnly) {
        const bufferCount = s.qoe_buffer_count || 0;
        const errorCount = s.qoe_error_count || 0;
        const bufferTime = s.qoe_buffer_time_seconds || 0;
        // Consider it a problem if: >5 buffers, any errors, or >30s buffer time
        if (bufferCount <= 5 && errorCount === 0 && bufferTime <= 30) return false;
      }
      
      return true;
    });
  }, [currentSessions, filterCamera, filterUser, filterType, showAnonymousOnly, showProblemsOnly]);

  // Group filtered sessions
  const groupedSessions = useMemo(() => {
    return groupSessions(filteredSessions);
  }, [filteredSessions]);

  const handleKickSession = async () => {
    if (!kickSession) return;
    setIsKicking(true);
    try {
      const sessionId = kickSession._id || kickSession.id || kickSession.session_id;
      await api.kickSession(sessionId);
      toast.success('Session kicked successfully');
      setKickSession(null);
      fetchActiveSessions();
    } catch (err) {
      toast.error(err.message || 'Failed to kick session');
    } finally {
      setIsKicking(false);
    }
  };

  const handleKickUser = async () => {
    if (!kickUser) return;
    setIsKicking(true);
    try {
      await api.kickUser(kickUser);
      toast.success(`All sessions for ${kickUser} kicked`);
      setKickUser(null);
      fetchActiveSessions();
    } catch (err) {
      toast.error(err.message || 'Failed to kick user');
    } finally {
      setIsKicking(false);
    }
  };

  const handleEndStaleSessions = async () => {
    setIsEndingStale(true);
    try {
      const result = await api.endStaleSessions(30);
      if (result.ended_count > 0) {
        toast.success(`Marked ${result.ended_count} stale sessions as ended`);
      } else {
        toast.info('No stale sessions found');
      }
      fetchSessions();
    } catch (err) {
      toast.error(err.message || 'Failed to end stale sessions');
    } finally {
      setIsEndingStale(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sessions-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sessions</h1>
          <p className="text-muted-foreground">Monitor and manage viewing sessions</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleEndStaleSessions}
            disabled={isEndingStale}
            data-testid="end-stale-sessions-btn"
          >
            <Clock className={`mr-2 h-4 w-4 ${isEndingStale ? 'animate-pulse' : ''}`} />
            {isEndingStale ? 'Ending...' : 'End Stale'}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchSessions}
            disabled={isLoading}
            data-testid="refresh-sessions-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Active/History Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-sm grid-cols-2">
          <TabsTrigger value="active" data-testid="tab-active">
            <Activity className="mr-2 h-4 w-4" />
            Active
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="mr-2 h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Stats Row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 mt-6">
          <Card className="border-border bg-card">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Radio className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{filteredSessions.length}</p>
                <p className="text-sm text-muted-foreground">
                  {activeTab === 'active' ? 'Active Sessions' : 'Historical Sessions'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10">
                <Users className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{groupedSessions.length}</p>
                <p className="text-sm text-muted-foreground">Unique Viewers</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
                <Camera className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {filteredSessions.filter(s => s.session_type !== 'radio').length}
                </p>
                <p className="text-sm text-muted-foreground">Camera</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-violet-500/10">
                <Radio className="h-6 w-6 text-violet-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {filteredSessions.filter(s => s.session_type === 'radio').length}
                </p>
                <p className="text-sm text-muted-foreground">Radio</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-border bg-card mt-4">
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <Select value={filterCamera} onValueChange={setFilterCamera}>
              <SelectTrigger className="w-48" data-testid="filter-camera-select">
                <Camera className="mr-2 h-4 w-4" />
                <SelectValue placeholder="All Cameras" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cameras/Stations</SelectItem>
                {cameras.map(cam => (
                  <SelectItem key={cam} value={cam}>{cam}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Filter by user/device..."
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="w-48"
              data-testid="filter-user-input"
            />
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-36" data-testid="filter-type-select">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="camera">Camera</SelectItem>
                <SelectItem value="radio">Radio</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={showAnonymousOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAnonymousOnly(!showAnonymousOnly)}
              data-testid="filter-anonymous-btn"
            >
              <EyeOff className="mr-2 h-4 w-4" />
              Anonymous Only
            </Button>
            <Button
              variant={showProblemsOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setShowProblemsOnly(!showProblemsOnly)}
              className={showProblemsOnly ? "bg-red-500 hover:bg-red-600" : ""}
              data-testid="filter-problems-btn"
            >
              <AlertCircle className="mr-2 h-4 w-4" />
              Problems Only
            </Button>
          </CardContent>
        </Card>

        {/* Sessions Content */}
        <TabsContent value="active" className="mt-4">
          <div className="space-y-4">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            ) : error ? (
              <Card className="border-destructive/50 bg-destructive/10">
                <CardContent className="flex items-center gap-4 p-6">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Failed to load sessions</p>
                    <p className="text-sm text-muted-foreground">{error}</p>
                  </div>
                  <Button variant="outline" onClick={fetchSessions}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : groupedSessions.length === 0 ? (
              <Card className="border-border bg-card">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Users className="mb-4 h-12 w-12 text-muted-foreground/30" />
                  <p className="text-lg font-medium text-foreground">No active sessions</p>
                  <p className="text-sm text-muted-foreground">
                    {filterCamera !== 'all' || filterUser || filterType !== 'all' || showAnonymousOnly 
                      ? 'Try adjusting your filters' 
                      : 'No one is currently watching streams'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {groupedSessions.map((group) => (
                  <UserSessionGroup
                    key={group.key}
                    group={group}
                    onKickSession={setKickSession}
                    onKickUser={setKickUser}
                    onCopyDeviceId={handleCopyDeviceId}
                    showKick={true}
                  />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <div className="space-y-4">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            ) : error ? (
              <Card className="border-destructive/50 bg-destructive/10">
                <CardContent className="flex items-center gap-4 p-6">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Failed to load history</p>
                    <p className="text-sm text-muted-foreground">{error}</p>
                  </div>
                  <Button variant="outline" onClick={fetchSessions}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : groupedSessions.length === 0 ? (
              <Card className="border-border bg-card">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <History className="mb-4 h-12 w-12 text-muted-foreground/30" />
                  <p className="text-lg font-medium text-foreground">No session history</p>
                  <p className="text-sm text-muted-foreground">
                    {filterCamera !== 'all' || filterUser || filterType !== 'all' || showAnonymousOnly 
                      ? 'Try adjusting your filters' 
                      : 'No historical sessions found'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="space-y-3">
                  {groupedSessions.map((group) => (
                    <UserSessionGroup
                      key={group.key}
                      group={group}
                      onKickSession={setKickSession}
                      onKickUser={setKickUser}
                      onCopyDeviceId={handleCopyDeviceId}
                      showKick={false}
                    />
                  ))}
                </div>
                
                {hasMoreHistory && (
                  <div className="flex justify-center pt-4">
                    <Button 
                      variant="outline" 
                      onClick={loadMoreHistory}
                      data-testid="load-more-history-btn"
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Kick Session Dialog */}
      <Dialog open={!!kickSession} onOpenChange={() => setKickSession(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kick Session</DialogTitle>
            <DialogDescription>
              Are you sure you want to kick this session for user "{kickSession?.username || 'Anonymous'}"?
              They will be disconnected from the current stream.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKickSession(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleKickSession}
              disabled={isKicking}
              data-testid="confirm-kick-session-btn"
            >
              {isKicking ? 'Kicking...' : 'Kick Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kick User Dialog */}
      <Dialog open={!!kickUser} onOpenChange={() => setKickUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kick All Sessions</DialogTitle>
            <DialogDescription>
              Are you sure you want to kick ALL sessions for "{kickUser}"?
              They will be disconnected from all active streams.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKickUser(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleKickUser}
              disabled={isKicking}
              data-testid="confirm-kick-user-btn"
            >
              {isKicking ? 'Kicking...' : 'Kick All Sessions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Sessions;
